import {Grid} from 'src/design/atoms/Grid';
import React, {useCallback, useRef, useState} from 'react';
import type {
  NexusGraphRequest,
  NexusGraphResponse,
  NexusInFlightResponse,
} from 'src/api/ProactApiClient';
import {useLazyGetNexusGraphQuery} from 'src/redux/queries/workflowNexus';
import {useAlert} from 'src/shared/hooks/useAlert';
import {InFlightWorkflows} from './InFlightWorkflows';
import type {SearchSummary} from './NexusDetailPanel';
import {NexusDetailPanel} from './NexusDetailPanel';

export const NexusTab: React.FC = () => {
  const [triggerGraphRaw, {data: graphData, isFetching: isGraphLoading}] =
    useLazyGetNexusGraphQuery();
  const lastGraphArgs = useRef<NexusGraphRequest | null>(null);
  const {setAlert} = useAlert();

  const triggerGraph = useCallback(
    (args: NexusGraphRequest, preferCacheValue?: boolean) => {
      lastGraphArgs.current = args;
      return triggerGraphRaw(args, preferCacheValue);
    },
    [triggerGraphRaw]
  );

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(
    null
  );
  const [selectedWorkflow, setSelectedWorkflow] =
    useState<NexusInFlightResponse | null>(null);
  const [searchSummary, setSearchSummary] = useState<SearchSummary | null>(
    null
  );

  const handleSelectWorkflow = useCallback(
    async (workflow: NexusInFlightResponse) => {
      setSelectedWorkflowId(workflow.workflowId);
      setSelectedWorkflow(workflow);
      setSearchSummary(null);
      try {
        await triggerGraph({
          entityType: 'workflow',
          entityId: workflow.workflowId,
          maxDepth: 5,
        }).unwrap();
      } catch (error: any) {
        setAlert(error?.message ?? 'Failed to load workflow graph', 'error');
      }
    },
    [triggerGraph, setAlert]
  );

  const handleSearchSelect = useCallback(
    (_entityType: string, _entityId: number) => {
      // Clear in-flight selection — search results populate via graph entry-point
      setSelectedWorkflowId(null);
      setSelectedWorkflow(null);
      setSearchSummary(null);
    },
    []
  );

  const handleRefreshGraph = useCallback(() => {
    if (lastGraphArgs.current) void triggerGraph(lastGraphArgs.current);
  }, [triggerGraph]);

  const handleOpenBlockingEntity = useCallback(
    async (entityType: string, entityId: number) => {
      setSelectedWorkflowId(null);
      setSelectedWorkflow(null);
      setSearchSummary(null);
      try {
        await triggerGraph({entityType, entityId, maxDepth: 5}).unwrap();
      } catch (error: any) {
        setAlert(error?.message ?? 'Failed to load graph', 'error');
      }
    },
    [triggerGraph, setAlert]
  );

  // Extract summary from graph entry-point node when loaded via search
  const effectiveSearchSummary =
    !selectedWorkflow && graphData
      ? extractSearchSummary(graphData)
      : searchSummary;

  return (
    <Grid>
      <Grid.Col span={{base: 12, lg: 5}}>
        <InFlightWorkflows
          selectedWorkflowId={selectedWorkflowId}
          onSelectWorkflow={handleSelectWorkflow}
        />
      </Grid.Col>
      <Grid.Col span={{base: 12, lg: 7}}>
        <NexusDetailPanel
          selectedWorkflow={selectedWorkflow}
          graphData={graphData ?? null}
          isGraphLoading={isGraphLoading}
          searchSummary={effectiveSearchSummary}
          onOpenBlockingEntity={handleOpenBlockingEntity}
          onRefresh={handleRefreshGraph}
          triggerGraph={triggerGraph}
          onSearchSelect={handleSearchSelect}
        />
      </Grid.Col>
    </Grid>
  );
};

function extractSearchSummary(
  graphData: NexusGraphResponse
): SearchSummary | null {
  if (!graphData.entryPoint || graphData.nodes.length === 0) return null;

  const entryNode = graphData.nodes.find(n => n.id === graphData.entryPoint);
  if (!entryNode) return null;

  return {
    definitionName:
      entryNode.definitionName ??
      entryNode.subject ??
      entryNode.name ??
      entryNode.courseName ??
      entryNode.itemName,
    internalIdentifier: entryNode.internalIdentifier,
    state: entryNode.state ?? undefined,
    createdAt: entryNode.createdAt ?? undefined,
  };
}
